
import { useState, useEffect } from 'react';
import { unfurlLinkAction } from '@/app/actions';
import type { UnfurlLinkOutput } from '@/app/actions';
import { useToast } from './use-toast';

interface UseUnfurlResult {
  data: UnfurlLinkOutput | null;
  isLoading: boolean;
  error: string | null;
}

// A simple in-memory cache
const cache = new Map<string, UnfurlLinkOutput>();

export function useUnfurl(url: string): UseUnfurlResult {
  const [result, setResult] = useState<UseUnfurlResult>({
    data: null,
    isLoading: true,
    error: null,
  });
  const { toast } = useToast();

  useEffect(() => {
    // Reset state when URL changes
    setResult({ data: null, isLoading: true, error: null });

    if (!url) {
      setResult({ data: null, isLoading: false, error: 'No URL provided.' });
      return;
    }

    // Check cache first
    if (cache.has(url)) {
        setResult({ data: cache.get(url)!, isLoading: false, error: null });
        return;
    }

    const unfurl = async () => {
      try {
        const response = await unfurlLinkAction(url);
        if (response.success) {
          // Cache the successful response
          cache.set(url, response.data);
          setResult({ data: response.data, isLoading: false, error: null });
        } else {
          setResult({ data: null, isLoading: false, error: response.error });
          toast({
            variant: "destructive",
            title: "Could not preview link",
            description: response.error,
          });
        }
      } catch (e: any) {
        const errorMessage = e.message || 'An unknown error occurred.';
        setResult({ data: null, isLoading: false, error: errorMessage });
        toast({
            variant: "destructive",
            title: "Could not preview link",
            description: errorMessage,
        });
      }
    };

    unfurl();

  }, [url, toast]);

  return result;
}
